import React, { useState } from "react";
import { Labeled, ReferenceField, TextField, useNotify, useRefresh } from "react-admin";
import { Box, Button, Checkbox, FormGroup, FormControlLabel, TextField as TextInput } from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";

import { CampaignSubTypes } from "utils/constants";
import { settings } from "app";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { administrationRequests } from "api/administrationApi";
import { candidateRequests } from "api/candidateApi";
import dataProvider from "server/dataProvider";

const FULL_WIDTH_LABELS = ["Избрани преференции", "Подписани декларации"];

const CAMPAIGNS_WITHOUT_DIPLOMAS = [CampaignSubTypes.Preliminary, CampaignSubTypes.HospitalContract];

const hospitalContractData = record => [
  {
    id: "declarationDocuments",
    value: record?.declarationDocuments?.length
      ? record.declarationDocuments.map(x => `file-${x.documentId}.pdf`).join(", ")
      : "Няма качени декларации",
    label: "Подписани декларации",
    file: false,
  },
  {
    id: "hospitalId",
    value: (
      <ReferenceField
        record={record}
        source="hospitalityId"
        reference={administrationRequests.getHospitalitiesSelect()}
        link={false}
        emptyText="-"
      >
        <TextField source="nameBg" />
      </ReferenceField>
    ),
    label: "Болница",
    file: false,
  },
];

const stringData = record => {
  const regularStringsData = [
    { id: "firstName", value: record?.info?.firstName, label: "Име", file: false },
    { id: "secondName", value: record?.info?.secondName, label: "Презиме", file: false },
    { id: "lastName", value: record?.info?.lastName, label: "Фамилия", file: false },
    { id: "personId", value: record?.info?.personId, label: "ЕГН", file: false },
    { id: "contactPhone", value: record?.info?.contactPhone, label: "Телефон за контакт", file: false },
    {
      id: "gender",
      value: record?.info?.gender === 1 ? "Мъж" : "Жена",
      label: "Пол",
      file: false,
    },
    {
      id: "preference",
      value: record?.preferences?.length > 0 ? record.preferences.map(x => x.preferenceName).join(", ") : null,
      label: "Избрани преференции",
      file: false,
    },
    {
      id: "diplomas",
      value: record?.diplomaDocumentIds,
      label: "Диплома за средно образование",
      file: true,
    },
  ];

  const data = CAMPAIGNS_WITHOUT_DIPLOMAS.includes(record?.campaignSubTypeId)
    ? regularStringsData.filter(x => x.id !== "diplomas")
    : regularStringsData;

  return record?.campaignSubTypeId === CampaignSubTypes.HospitalContract
    ? [...data, ...hospitalContractData(record)]
    : data;
};

const ReturnEdittingModal = ({ candidacyId, record }) => {
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [message, setMessage] = useState("");

  const notify = useNotify();
  const refresh = useRefresh();

  const handleConfirm = async () => {
    await dataProvider.customRequest(
      candidateRequests.returnCandidacyForCorrection(candidacyId),
      {
        fields: selectedChoices,
        message,
      },
      "POST",
    );

    await dataProvider.customRequest(candidateRequests.updateCandidacyNote(candidacyId), { note: message }, "PUT");

    notify("Успешно изпратена заявка за корекция.", { type: "success" });
    refresh();
  };

  const resetState = () => {
    setSelectedChoices([]);
    setMessage("");
  };

  const handleChange = id => {
    setSelectedChoices(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
  };

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
    >
      <ConfirmModal
        disableBackdropClose={true}
        triggerButton={
          <Button
            variant="contained"
            color="inherit"
            size="medium"
            startIcon={<ModeEditIcon />}
          >
            Върни кандидатура за корекция
          </Button>
        }
        titleContent="Моля отбележете полетата които да бъдат върнати за корекция"
        saveTitle="Изпрати"
        closeTitle="Откажи"
        onConfirm={handleConfirm}
        resetProps={resetState}
        confirmEnabled={selectedChoices.length > 0}
        dialogProps={{
          sx: {
            "& .MuiDialog-paper": {
              textAlign: "center",
              minWidth: "50%",
            },
            "& .MuiDialogContent-root": {
              overflow: "hidden !important",
            },
          },
        }}
      >
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: ".5rem",
            marginBottom: "1.5em",
            ...settings.style.shadowBox,
          }}
        >
          {stringData(record).map(item =>
            item.value ? (
              <Labeled
                key={item.id}
                label={item.label}
                sx={{
                  textAlign: "left",
                  gridColumn: FULL_WIDTH_LABELS.includes(item.label) ? "span 3" : "auto",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedChoices.includes(item.id)}
                      onChange={() => handleChange(item.id)}
                    />
                  }
                  label={
                    item.file && Array.isArray(item.value)
                      ? item.value.map(id => `file-${id}.pdf`).join(", ")
                      : item.value
                  }
                />
              </Labeled>
            ) : null,
          )}
        </FormGroup>

        <TextInput
          label="Въведи съобщение"
          multiline
          variant="outlined"
          onChange={e => setMessage(e.target.value)}
          maxRows={6}
          minRows={4}
          fullWidth
        />
      </ConfirmModal>
    </Box>
  );
};

export default ReturnEdittingModal;
